import { useTranslation } from "react-i18next";

import { ISavCustomerName } from "../../../../../data/useSav";
import { CallOutcome } from "../../../../../models/CallQueue";
import classNames from "../../../../../utils/classNames";
import { callBackPresets, OUTCOME_LABELS, OUTCOME_ORDER, toDateTimeLocal } from "../../../../../utils/callQueue";
import PriceFormat from "../../../../../utils/priceFormat";

export interface OutcomeDraft {
  outcome: CallOutcome | null;
  callBackAt: string;
  assignToMe: boolean;
  customerFigure: string;
  referToCustomerId: string;
  notes: string;
}

const outcomeStyle = (outcome: CallOutcome, selected: boolean) => {
  if (outcome === CallOutcome.Booked)
    return selected ? "border-2 border-green-700 bg-green-50 text-green-800" : "border border-green-200 bg-green-50 text-green-800";
  if (outcome === CallOutcome.CallBack)
    return selected ? "border-2 border-amber-700 bg-amber-50 text-amber-800" : "border border-amber-200 bg-white text-amber-800";
  return selected ? "border-2 border-gray-700 bg-gray-50 text-gray-900" : "border border-gray-300 bg-white text-gray-700";
};

export default function CallOutcomePanel({
  draft,
  setDraft,
  agreedPrice,
  isSav,
  atfNames,
  autoDial,
  setAutoDial,
  countdown,
  onCancelCountdown,
  onSave,
  isSaving,
  error,
  disabled,
}: {
  draft: OutcomeDraft;
  setDraft: (draft: OutcomeDraft) => void;
  agreedPrice: string;
  isSav: boolean;
  atfNames: ISavCustomerName[];
  autoDial: boolean;
  setAutoDial: (value: boolean) => void;
  countdown: number | null;
  onCancelCountdown: () => void;
  onSave: () => void;
  isSaving: boolean;
  error: string | null;
  disabled: boolean;
}) {
  const { t } = useTranslation("leads");
  const update = (patch: Partial<OutcomeDraft>) => setDraft({ ...draft, ...patch });
  const presets = callBackPresets(new Date());

  return (
    <section aria-label={t("Call outcome")} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-baseline justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-[15px] font-semibold text-gray-900">{t("Call outcome")}</h2>
        <span className="text-xs text-gray-600">{t("Required")}</span>
      </div>

      <div className="flex flex-col gap-3.5 px-4 py-3.5">
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("Call outcome")}>
          {OUTCOME_ORDER.map((outcome) => (
            <button
              key={outcome}
              type="button"
              role="radio"
              aria-checked={draft.outcome === outcome}
              disabled={disabled}
              onClick={() => update({ outcome })}
              className={classNames(
                "min-h-[44px] rounded-md px-2.5 py-2 text-left text-[13px] disabled:opacity-50",
                draft.outcome === outcome ? "font-semibold" : "font-medium",
                outcomeStyle(outcome, draft.outcome === outcome)
              )}
            >
              {t(OUTCOME_LABELS[outcome])}
            </button>
          ))}
        </div>

        {draft.outcome === CallOutcome.CallBack && (
          <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <label className="flex flex-col gap-1 text-xs font-semibold text-amber-800">
              {t("Call back when?")}
              <input
                type="datetime-local"
                value={draft.callBackAt}
                onChange={(e) => update({ callBackAt: e.target.value })}
                className="min-h-[44px] rounded-md border-amber-200 text-sm font-normal text-gray-900"
              />
            </label>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => update({ callBackAt: toDateTimeLocal(preset.at) })}
                  className="rounded-full border border-amber-200 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                >
                  {t(preset.label)}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-amber-800">
              <input
                type="checkbox"
                checked={draft.assignToMe}
                onChange={(e) => update({ assignToMe: e.target.checked })}
                className="rounded border-amber-300 text-amber-700 focus:ring-amber-700"
              />
              {t("Assign to me, not the pool")}
            </label>
          </div>
        )}

        {draft.outcome === CallOutcome.PriceTooLow && (
          <label className="flex flex-col gap-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
            {t("What does the customer want?")}
            <span className="flex min-h-[44px] items-center rounded-md border border-amber-200 bg-white px-2.5">
              <span className="text-gray-500">£</span>
              <input
                type="text"
                inputMode="decimal"
                value={draft.customerFigure}
                onChange={(e) => update({ customerFigure: e.target.value.replace(/[^\d.]/g, "") })}
                className="w-full border-0 bg-transparent py-0 pl-1 text-sm font-normal text-gray-900 focus:ring-0"
              />
            </span>
            <span className="font-normal">{t("It goes to Manager Assistance with this figure.")}</span>
          </label>
        )}

        {draft.outcome === CallOutcome.Booked && (
          <div className="flex flex-col gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-800">
            <span className="font-semibold">
              {t("Booking at {{price}}", { price: agreedPrice ? PriceFormat(Number(agreedPrice)) : "—" })}
            </span>
            {isSav && (
              <label className="flex flex-col gap-1 font-medium">
                {t("Who collects it?")}
                <select
                  value={draft.referToCustomerId}
                  onChange={(e) => update({ referToCustomerId: e.target.value })}
                  className="min-h-[44px] rounded-md border-green-200 text-sm text-gray-900"
                >
                  <option value="">{t("We collect it ourselves")}</option>
                  {atfNames.map((atf) => (
                    <option key={atf.id} value={atf.id}>{t("Refer to {{name}}", { name: atf.name })}</option>
                  ))}
                </select>
              </label>
            )}
            <span>{t("Trip Manager schedules the collection.")}</span>
          </div>
        )}

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          {t("Notes")}
          <textarea
            rows={4}
            value={draft.notes}
            onChange={(e) => update({ notes: e.target.value })}
            className="resize-y rounded-md border-gray-300 text-[13px] text-gray-900"
          />
        </label>

        {error && <p className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-700" role="alert">{error}</p>}

        {countdown !== null ? (
          <button
            type="button"
            onClick={onCancelCountdown}
            className="min-h-[48px] rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            {t("Dialling the next customer in {{n}}s · Cancel", { n: countdown })}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSave}
            disabled={disabled || draft.outcome === null || isSaving}
            className="min-h-[48px] rounded-md border border-primary-600 bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? t("Saving…") : t("Save & next call")}
          </button>
        )}
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={autoDial}
            onChange={(e) => setAutoDial(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-600"
          />
          {t("Dial next customer automatically after 10s")}
        </label>
      </div>
    </section>
  );
}
